// Phase 7 — Anomaly + Recommendations (v2-native).
//
// Visual primitives mirror `_new/detection/WAFEvent.js` via the
// shared `asecEvent*` tokens in `asecUi.js` — severity pills,
// status dots, mono-blue path/identity cells, skeleton loaders,
// hoverable rows. Three tabs (Findings / Baselines / Recommendations)
// share the same look so operators see one coherent product.
import React from "react";
import {
  Stack, Box, Typography, Button, Tabs, Tab,
  Table, TableBody, TableCell, TableHead, TableRow, Skeleton, Chip,
  Tooltip, Select, MenuItem, TablePagination, IconButton,
  Dialog, DialogContent, DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { EyeOff } from "lucide-react";
import asec from "../../../../../utils/axios/v1/asecAxios";
import SectionHelp from "./components/SectionHelp";
import colors from "../../../../../utils/colors";
import { formatDate } from "../../../../../utils/format";
import AsecPage from "./AsecPage";
import { useNotification } from "../../../../../contexts/NotificationContext";
import { SafeTextField } from "../../../../common/Greens";
import LayerdIcon from "../../../../common/LayerdIcon";
import {
  asecBtnOutlinedSm, asecBtnPrimarySm, asecTabsSx, filterSelectSx,
  asecEventTableBodySx, asecMonoBlueSx, asecEventRowSx,
  asecFindingsTableSx, asecSeverityConfig, asecStatusDotColor,
  asecDialogActionsSx, asecIconButtonSx, asecFieldLabelSx,
} from "./asecUi";
import {
  AsecEventsHeadCell,
  AsecEventsBodyText,
  AsecEventsActivityCell,
  AsecEventsHostnameCell,
  AsecEventsViewCell,
} from "./components/AsecEventsTable";
import { AsecEventsFilterBar } from "./components/AsecEventsFilterBar";
import AnomalyFindingDrawer from "./components/AnomalyFindingDrawer";
import RefreshIconButton from "../../../../common/RefreshIconButton";

const ROWS_PER_PAGE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DEFAULT_PAGE_SIZE = 25;

const tablePaginationSx = {
  ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
    fontSize: 14, color: colors.neutral[700],
  },
};

const FINDING_STATUS_FILTERS = [
  { value: "all", label: "All statuses" },
  { value: "open", label: "Open only" },
  { value: "suppressed", label: "Suppressed" },
  { value: "resolved", label: "Resolved" },
];

const BASELINE_METRIC_FILTERS = [
  { value: "", label: "All metrics" },
  { value: "requests_per_second", label: "req/sec" },
  { value: "error_rate", label: "error rate" },
  { value: "latency_p50_ms", label: "latency p50" },
  { value: "latency_p99_ms", label: "latency p99" },
  { value: "payload_size_bytes", label: "payload size" },
];

const BASELINE_METHOD_FILTERS = [
  { value: "", label: "All methods" },
  { value: "GET", label: "GET" },
  { value: "POST", label: "POST" },
  { value: "PUT", label: "PUT" },
  { value: "PATCH", label: "PATCH" },
  { value: "DELETE", label: "DELETE" },
  { value: "HEAD", label: "HEAD" },
  { value: "OPTIONS", label: "OPTIONS" },
];

const BASELINE_CONFIDENCE_FILTERS = [
  { value: "", label: "All samples" },
  { value: "trusted", label: "Trusted (≥100)" },
  { value: "low", label: "Low confidence (<100)" },
];

function getBaselinesEmptyMessage({ search, metric, method, confidence }) {
  if (search || metric || method || confidence) {
    return "No baselines match the current filters.";
  }
  return "No baselines yet. The learner runs every 5 minutes; baselines appear once an endpoint has ≥100 samples in the 7-day window.";
}

function getFindingsEmptyMessage(statusFilter) {
  switch (statusFilter) {
    case "resolved":
      return "No resolved anomalies for the current filters.";
    case "suppressed":
      return "No suppressed anomalies for the current filters.";
    case "all":
      return "No anomalies recorded yet.";
    default:
      return "No open anomalies. The detector runs every 5 minutes — findings appear when traffic deviates from the baseline by ≥3σ.";
  }
}

function formatFindingActionMessage(body) {
  if (body?.action === "resolve") return "Anomaly resolved.";
  if (body?.action === "suppress") return "Anomaly suppressed for 7 days.";
  return "Anomaly updated.";
}

function SuppressReasonDialog({ open, onClose, onConfirm, saving }) {
  const [reason, setReason] = React.useState("");
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setReason("");
      setError("");
    }
  }, [open]);

  const handleConfirm = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setError("Enter a reason — suppression requires one and lasts 7 days.");
      return;
    }
    const ok = await onConfirm(trimmed);
    if (ok) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(17, 17, 17, 0.45)" } },
      }}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow: "0 16px 48px rgba(17, 17, 17, 0.14)",
          overflow: "hidden",
          backgroundColor: "#fff",
        },
      }}
    >
      <Box sx={{ px: 3, pt: 3, pb: 0, backgroundColor: "#fff" }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "10px",
                bgcolor: colors.primary[100],
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <LayerdIcon icon={EyeOff} size="md" color="primary" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: "Rubik",
                  fontSize: 18,
                  fontWeight: 600,
                  lineHeight: "26px",
                  color: colors.neutral[1000],
                }}
              >
                Suppress anomaly
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Rubik",
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: colors.neutral[600],
                  mt: 0.5,
                }}
              >
                This finding will be hidden from the open list for 7 days. Add a short reason so your team knows why it was suppressed.
              </Typography>
            </Box>
          </Stack>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            disabled={saving}
            sx={{ ...asecIconButtonSx, mt: -0.5, mr: -0.5 }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      </Box>
      <DialogContent sx={{ px: 3, pt: 2.5, pb: 1, backgroundColor: "#fff" }}>
        <Typography component="label" sx={{ ...asecFieldLabelSx, mb: 1 }}>
          Reason
        </Typography>
        <SafeTextField
          autoFocus
          multiline
          minRows={3}
          fullWidth
          placeholder="e.g. Expected traffic spike during release window"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (error) setError("");
          }}
          error={Boolean(error)}
          helperText={error || "Required"}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              fontSize: 14,
              fontFamily: "Rubik",
              backgroundColor: "#fff",
              "& fieldset": { borderColor: colors.neutral[200] },
              "&:hover fieldset": { borderColor: colors.neutral[300] },
              "&.Mui-focused fieldset": { borderColor: colors.primary[300] },
            },
            "& .MuiFormHelperText-root": {
              fontFamily: "Rubik",
              fontSize: 12,
              color: error ? undefined : colors.neutral[600],
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ ...asecDialogActionsSx, justifyContent: "flex-end", backgroundColor: "#fff" }}>
        <Button onClick={onClose} disabled={saving} sx={asecBtnOutlinedSm}>Cancel</Button>
        <Button onClick={handleConfirm} disabled={saving} sx={asecBtnPrimarySm}>
          {saving ? "Suppressing…" : "Suppress for 7 days"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SkeletonRows({ rows = ROWS_PER_PAGE, cols }) {
  return (
    <TableBody>
      {Array.from({ length: rows }).map((_, r) => (
        <TableRow key={`sk_${r}`}>
          {Array.from({ length: cols }).map((__, c) => (
            <TableCell key={`sk_${r}_${c}`} sx={{ py: 2 }}>
              <Skeleton variant="rectangular" height={22} sx={{ borderRadius: "11px" }} />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );
}

function SeverityPill({ severity }) {
  const c = asecSeverityConfig(severity);
  return (
    <Chip
      size="small"
      label={c.label}
      sx={{
        px: 1, height: 22,
        background: c.bgColor, color: c.color,
        borderRadius: "30px",
fontSize: 12, fontWeight: 500,
      }}
    />
  );
}

function StatusCell({ resolvedAt, suppressedUntil }) {
  const state = resolvedAt ? "resolved" : suppressedUntil ? "suppressed" : "open";
  const label = state[0].toUpperCase() + state.slice(1);
  return (
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: asecStatusDotColor(state) }} />
      <Typography sx={asecEventTableBodySx}>{label}</Typography>
    </Stack>
  );
}

// `traffic.rps_spike_high` → "TRAFFIC · Rps spike high"
function prettifyKind(kind) {
  if (!kind) return "—";
  const dot = kind.indexOf(".");
  if (dot < 0) return kind;
  const family = kind.slice(0, dot).toUpperCase();
  const tail = kind.slice(dot + 1).replace(/_/g, " ");
  return `${family} · ${tail[0].toUpperCase() + tail.slice(1)}`;
}

function headCellAlign(i, colCount) {
  if (i === colCount - 1) return "right";
  return "left";
}

function FindingsTable({ findings, statusFilter, onOpen }) {
  const cols = ["Hostname", "Activity", "Anomaly", "Identity", "Severity", "Status", "Detected", "Action"];
  if (findings === null) {
    return <SkeletonTable cols={cols} />;
  }
  if (findings.length === 0) {
    return (
      <EmptyTable cols={cols}>
        {getFindingsEmptyMessage(statusFilter)}
      </EmptyTable>
    );
  }
  return (
    <Table sx={asecFindingsTableSx}>
      <TableHead><TableRow>
        {cols.map((h, i) => (
          <AsecEventsHeadCell key={h || "view"} align={headCellAlign(i, cols.length)}>
            {h}
          </AsecEventsHeadCell>
        ))}
      </TableRow></TableHead>
      <TableBody>
        {findings.map((r) => (
          <TableRow
            key={r.id}
            onClick={() => onOpen?.(r)}
            sx={asecEventRowSx}
          >
            <TableCell>
              <AsecEventsHostnameCell host={r.host} siteId={r.site_id} />
            </TableCell>
            <TableCell>
              <AsecEventsActivityCell method={r.method} path={r.path_template} />
            </TableCell>
            <TableCell>
              <Stack>
                <Typography sx={asecEventTableBodySx}>{prettifyKind(r.finding_kind)}</Typography>
                <Typography sx={asecMonoBlueSx}>{r.finding_kind}</Typography>
              </Stack>
            </TableCell>
            <TableCell>
              {r.identity_kind ? (
                <Tooltip title={r.identity_id || ""} arrow>
                  <Typography sx={asecMonoBlueSx}>
                    {r.identity_kind}:{(r.identity_id || "").slice(0, 12)}
                  </Typography>
                </Tooltip>
              ) : (
                <Typography sx={{ ...asecEventTableBodySx, color: colors.neutral[500] }}>—</Typography>
              )}
            </TableCell>
            <TableCell><SeverityPill severity={r.severity} /></TableCell>
            <TableCell>
              <StatusCell resolvedAt={r.resolved_at} suppressedUntil={r.suppressed_until} />
            </TableCell>
            <TableCell>
              <Typography sx={asecEventTableBodySx}>
                {r.detected_at ? formatDate(r.detected_at) : "—"}
              </Typography>
            </TableCell>
            <AsecEventsViewCell onClick={() => onOpen?.(r)} />
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function BaselinesTable({ baselines, allBaselines, emptyMessage }) {
  const cols = ["Endpoint", "Metric", "Mean", "P50", "P99", "Computed"];
  if (allBaselines === null) return <SkeletonTable cols={cols} />;
  if (allBaselines.length === 0) {
    return (
      <EmptyTable cols={cols}>
        {emptyMessage}
      </EmptyTable>
    );
  }
  return (
    <Table size="small">
      <TableHead><TableRow>
        {cols.map((h) => (
          <AsecEventsHeadCell key={h}>{h}</AsecEventsHeadCell>
        ))}
      </TableRow></TableHead>
      <TableBody>
        {(baselines || []).map((r, i) => (
          <TableRow key={i} sx={asecEventRowSx}>
            <TableCell>
              <Stack>
                <AsecEventsHostnameCell host={r.host} siteId={r.site_id} />
                <Typography sx={asecMonoBlueSx}>{r.method} {r.path_template}</Typography>
              </Stack>
            </TableCell>
            <TableCell>
              <Typography sx={asecEventTableBodySx}>{prettifyMetric(r.metric)}</Typography>
            </TableCell>
            <TableCell><Typography sx={asecEventTableBodySx}>{fmtNum(r.mean)}</Typography></TableCell>
            <TableCell><Typography sx={asecEventTableBodySx}>{fmtNum(r.p50)}</Typography></TableCell>
            <TableCell><Typography sx={asecEventTableBodySx}>{fmtNum(r.p99)}</Typography></TableCell>
            <TableCell>
              <Typography sx={asecEventTableBodySx}>
                {r.computed_at ? formatDate(r.computed_at) : "—"}
              </Typography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// Phase K.12: short preview text for a passing dry_run. Mirrors the
// snackbar text K.11 renders post-accept so the operator sees the
// same shape before AND after clicking.
function formatDryRunPreview(applied) {
  if (!applied) return "";
  if (applied.kind === "risk_config_upsert" && applied.row) {
    const r = applied.row;
    return (r.inline_enforce ? "ENFORCE " : "observe ") +
      "challenge=" + r.challenge_threshold + " deny=" + r.deny_threshold;
  }
  if (applied.kind === "risk_config_route_upsert" && applied.row) {
    const r = applied.row;
    const verb = applied.will_create === false ? "update" : "create";
    return verb + " " + r.method + " " + r.path_template +
      " (" + (r.inline_enforce ? "ENFORCE" : "observe") + ")";
  }
  return applied.kind || "";
}

function RecommendationsTable({ recs, allRecs, dryRuns, onAction }) {
  const cols = ["Recommendation", "Rationale", "Severity", "State", "Created", "Action"];
  if (allRecs === null) return <SkeletonTable cols={cols} />;
  if (allRecs.length === 0) {
    return (
      <EmptyTable cols={cols}>
        No recommendations. They synthesize from open anomalies + posture findings + sensitivity tags; the queue fills as those signals accumulate.
      </EmptyTable>
    );
  }
  return (
    <Table sx={asecFindingsTableSx}>
      <TableHead><TableRow>
        {cols.map((h, i) => (
          <AsecEventsHeadCell key={h || i} align={headCellAlign(i, cols.length)}>
            {h}
          </AsecEventsHeadCell>
        ))}
      </TableRow></TableHead>
      <TableBody>
        {(recs || []).map((r) => (
          <TableRow key={r.id} sx={asecEventRowSx}>
            <TableCell>
              <Stack>
                <Typography sx={{ ...asecEventTableBodySx, fontWeight: 500 }}>{r.title}</Typography>
                <Typography sx={asecMonoBlueSx}>{r.rec_kind}</Typography>
              </Stack>
            </TableCell>
            <TableCell sx={{ maxWidth: 480 }}>
              <Typography sx={{ ...asecEventTableBodySx, fontSize: 14, color: colors.neutral[700] }}>
                {r.rationale}
              </Typography>
            </TableCell>
            <TableCell><SeverityPill severity={r.severity} /></TableCell>
            <TableCell>
              <Chip label={r.state} size="small"
                sx={{
fontSize: 12,
                  background: r.state === "accepted" ? "#DCFCE7" : r.state === "rejected" ? "#FEE2E2" : colors.neutral[100],
                  color:      r.state === "accepted" ? "#15803D" : r.state === "rejected" ? "#B91C1C" : colors.neutral[700],
                  borderRadius: "30px",
                }} />
            </TableCell>
            <TableCell>
              <Typography sx={asecEventTableBodySx}>
                {r.created_at ? formatDate(r.created_at) : "—"}
              </Typography>
            </TableCell>
            <TableCell align="right">
              {/* Phase K.13: accepted recs with a captured revert_state
                  get a Revert button instead of pending-row actions. */}
              {r.state === "accepted" && r.revert_state && (
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button size="small" sx={asecBtnOutlinedSm}
                          onClick={() => onAction(r.id, "revert")}>
                    Revert
                  </Button>
                </Stack>
              )}
              {r.state === "pending" && (() => {
                // Phase K.12: dry_run gating. risk_score.* recs
                // either show a preview chip (ok), a disabled
                // Accept w/ tooltip (bad shape), or the original
                // Accept while the dry_run is still in flight.
                const isRiskScore = typeof r.rec_kind === "string"
                  && r.rec_kind.indexOf("risk_score.") === 0;
                const dr = dryRuns && dryRuns[r.id];
                let acceptDisabled = false;
                let acceptTooltip = "";
                let previewChip = null;
                if (isRiskScore) {
                  if (dr && dr.ok === true) {
                    const text = formatDryRunPreview(dr.applied_preview);
                    if (text) {
                      previewChip = (
                        <Chip size="small" label={"would: " + text}
                              sx={{ mr: 1, fontSize: 12, background: "#E0F2FE",
                                    color: "#075985", borderRadius: "12px" }} />
                      );
                    }
                  } else if (dr && dr.ok === false) {
                    acceptDisabled = true;
                    acceptTooltip = dr.message || "dry-run failed";
                  }
                  // dr undefined OR ok=null → leave Accept enabled
                  // and don't show a chip. We don't want to block
                  // operators on transient dry_run failures.
                }
                const acceptBtn = (
                  <Button size="small" sx={asecBtnPrimarySm}
                          disabled={acceptDisabled}
                          onClick={() => onAction(r.id, "accept")}>
                    Accept
                  </Button>
                );
                return (
                  <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                    {previewChip}
                    {acceptTooltip
                      ? <Tooltip title={acceptTooltip}><span>{acceptBtn}</span></Tooltip>
                      : acceptBtn}
                    <Button size="small" sx={asecBtnOutlinedSm} onClick={() => onAction(r.id, "reject")}>Reject</Button>
                    <Button size="small" sx={asecBtnOutlinedSm} onClick={() => onAction(r.id, "defer")}>Defer</Button>
                  </Stack>
                );
              })()}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// Small shared shells.
function SkeletonTable({ cols }) {
  return (
    <Table sx={asecFindingsTableSx}>
      <TableHead><TableRow>
        {cols.map((h, i) => (
          <AsecEventsHeadCell key={h || i} align={headCellAlign(i, cols.length)}>
            {h}
          </AsecEventsHeadCell>
        ))}
      </TableRow></TableHead>
      <SkeletonRows cols={cols.length} />
    </Table>
  );
}

function EmptyTable({ cols, children }) {
  return (
    <Table sx={asecFindingsTableSx}>
      <TableHead><TableRow>
        {cols.map((h, i) => (
          <AsecEventsHeadCell key={h || i} align={headCellAlign(i, cols.length)}>
            {h}
          </AsecEventsHeadCell>
        ))}
      </TableRow></TableHead>
      <TableBody><TableRow>
        <TableCell colSpan={cols.length}>
          <Typography textAlign="center" sx={{ ...asecEventTableBodySx, py: 4, color: colors.neutral[500] }}>
            {children}
          </Typography>
        </TableCell>
      </TableRow></TableBody>
    </Table>
  );
}

function prettifyMetric(m) {
  switch (m) {
    case "requests_per_second": return "req/sec";
    case "error_rate":          return "error rate";
    case "latency_p50_ms":      return "latency p50";
    case "latency_p99_ms":      return "latency p99";
    case "payload_size_bytes":  return "payload size";
    default: return m || "—";
  }
}

function fmtNum(v) {
  if (v == null) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  if (Math.abs(n) >= 1000) return n.toFixed(0);
  if (Math.abs(n) >= 1)    return n.toFixed(2);
  return n.toFixed(4);
}

function Body({ siteId }) {
  const { showSuccess, showError } = useNotification();
  const [sub, setSub] = React.useState(0);
  const [findings, setFindings] = React.useState(null);
  const [baselines, setBaselines] = React.useState(null);
  const [recs, setRecs] = React.useState(null);
  // Phase K.12: dry-run cache keyed by rec.id. Values are
  //   { ok: true, applied_preview: {...} }      (good shape)
  //   { ok: false, message: "..." }            (bad shape)
  //   undefined                                 (not fetched yet)
  // Only fetched for pending risk_score.* recs — others have no
  // server-side apply step so dry_run would be trivially "ok+null".
  const [dryRuns, setDryRuns] = React.useState({});
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [findingsPage, setFindingsPage] = React.useState(0);
  const [findingsPageSize, setFindingsPageSize] = React.useState(DEFAULT_PAGE_SIZE);
  const [findingsTotal, setFindingsTotal] = React.useState(0);
  const [baselinesPage, setBaselinesPage] = React.useState(0);
  const [baselinesPageSize, setBaselinesPageSize] = React.useState(DEFAULT_PAGE_SIZE);
  const [baselinesTotal, setBaselinesTotal] = React.useState(0);
  const [recsPage, setRecsPage] = React.useState(0);
  const [recsPageSize, setRecsPageSize] = React.useState(DEFAULT_PAGE_SIZE);
  const [recsTotal, setRecsTotal] = React.useState(0);
  const [suppressFindingId, setSuppressFindingId] = React.useState(null);
  const [suppressSaving, setSuppressSaving] = React.useState(false);
  const [drawerFinding, setDrawerFinding] = React.useState(null);
  const [actionSaving, setActionSaving] = React.useState(false);
  const [baselineMetricFilter, setBaselineMetricFilter] = React.useState("");
  const [baselineMethodFilter, setBaselineMethodFilter] = React.useState("");
  const [baselineConfidenceFilter, setBaselineConfidenceFilter] = React.useState("");
  const [baselineSearch, setBaselineSearch] = React.useState("");
  const [debouncedBaselineSearch, setDebouncedBaselineSearch] = React.useState("");

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedBaselineSearch(baselineSearch), 300);
    return () => clearTimeout(t);
  }, [baselineSearch]);

  React.useEffect(() => { setFindingsPage(0); }, [statusFilter]);
  React.useEffect(() => {
    setBaselinesPage(0);
  }, [baselineMetricFilter, baselineMethodFilter, baselineConfidenceFilter, debouncedBaselineSearch]);

  const loadDryRuns = React.useCallback((recList) => {
    const candidates = recList.filter((row) =>
      row && row.state === "pending"
        && typeof row.rec_kind === "string"
        && row.rec_kind.indexOf("risk_score.") === 0);
    if (candidates.length === 0) return;
    candidates.forEach((row) => {
      asec.recommendationDryRun(siteId, row.id)
        .then((resp) => {
          setDryRuns((prev) => ({ ...prev, [row.id]: resp.data }));
        })
        .catch((e) => {
          const data = e && e.response && e.response.data;
          if (data && data.ok === false) {
            setDryRuns((prev) => ({ ...prev, [row.id]: data }));
          } else {
            setDryRuns((prev) => ({
              ...prev,
              [row.id]: { ok: null, message: (e && e.message) || "dry_run failed" },
            }));
          }
        });
    });
  }, [siteId]);

  const reloadBaselines = React.useCallback(async () => {
    if (!siteId) return;
    setBaselines(null);
    const params = {
      page: baselinesPage,
      limit: baselinesPageSize,
    };
    if (baselineMetricFilter) params.metric = baselineMetricFilter;
    if (baselineMethodFilter) params.method = baselineMethodFilter;
    const q = String(debouncedBaselineSearch || "").trim();
    if (q) params.q = q;
    if (baselineConfidenceFilter) params.confidence = baselineConfidenceFilter;
    const b = await asec.anomalyBaselines(siteId, params).catch(() => ({ data: { items: [], total: 0 } }));
    const payload = b.data;
    const items = Array.isArray(payload?.items) ? payload.items : Array.isArray(payload) ? payload : [];
    setBaselines(items);
    setBaselinesTotal(typeof payload?.total === "number" ? payload.total : items.length);
  }, [
    siteId,
    baselineMetricFilter,
    baselineMethodFilter,
    baselineConfidenceFilter,
    debouncedBaselineSearch,
    baselinesPage,
    baselinesPageSize,
  ]);

  const reloadRecs = React.useCallback(async () => {
    if (!siteId) return;
    setRecs(null);
    setDryRuns({});
    const r = await asec.recommendations(siteId, {
      page: recsPage,
      limit: recsPageSize,
    }).catch(() => ({ data: { items: [], total: 0 } }));
    const payload = r.data;
    const recList = Array.isArray(payload?.items) ? payload.items : Array.isArray(payload) ? payload : [];
    setRecs(recList);
    setRecsTotal(typeof payload?.total === "number" ? payload.total : recList.length);
    loadDryRuns(recList);
  }, [siteId, loadDryRuns, recsPage, recsPageSize]);

  const reloadBaselinesRecs = React.useCallback(async () => {
    await Promise.all([reloadBaselines(), reloadRecs()]);
  }, [reloadBaselines, reloadRecs]);

  const reloadFindings = React.useCallback(async () => {
    if (!siteId) return;
    setFindings(null);
    const f = await asec.anomalyFindings(siteId, {
      page: findingsPage,
      limit: findingsPageSize,
      status: statusFilter,
    }).catch(() => ({ data: { items: [], total: 0 } }));
    setFindings(Array.isArray(f.data?.items) ? f.data.items : []);
    setFindingsTotal(typeof f.data?.total === "number" ? f.data.total : 0);
  }, [siteId, statusFilter, findingsPage, findingsPageSize]);

  const reload = React.useCallback(() => {
    reloadFindings();
    reloadBaselinesRecs();
  }, [reloadFindings, reloadBaselinesRecs]);

  React.useEffect(() => { reloadRecs(); }, [reloadRecs]);
  React.useEffect(() => { reloadBaselines(); }, [reloadBaselines]);
  React.useEffect(() => { reloadFindings(); }, [reloadFindings]);

  React.useEffect(() => {
    const maxPage = Math.max(0, Math.ceil((baselinesTotal || 0) / baselinesPageSize) - 1);
    if (baselinesPage > maxPage) setBaselinesPage(maxPage);
  }, [baselinesTotal, baselinesPage, baselinesPageSize]);

  React.useEffect(() => {
    const maxPage = Math.max(0, Math.ceil((recsTotal || 0) / recsPageSize) - 1);
    if (recsPage > maxPage) setRecsPage(maxPage);
  }, [recsTotal, recsPage, recsPageSize]);

  const findingAction = async (id, body) => {
    try {
      await asec.anomalyFindingPatch(siteId, id, body);
      showSuccess(formatFindingActionMessage(body));
      reloadFindings();
      return true;
    } catch (e) {
      const msg = (e && e.response && e.response.data && e.response.data.message)
        || (e && e.message) || String(e);
      showError(msg);
      return false;
    }
  };

  const resolveFinding = async (id) => {
    setActionSaving(true);
    try {
      return await findingAction(id, { action: "resolve" });
    } finally {
      setActionSaving(false);
    }
  };

  const confirmSuppress = async (reason) => {
    if (!suppressFindingId) return false;
    setSuppressSaving(true);
    try {
      const ok = await findingAction(suppressFindingId, { action: "suppress", reason });
      if (ok) setDrawerFinding(null);
      return ok;
    } finally {
      setSuppressSaving(false);
    }
  };

  // Phase K.11: render the K.10 server-side handler's `applied`
  // echo so the operator sees exactly what changed, not just
  // "Marked accept". Falls back to the original confirmation for
  // non-accept actions and for recs the handler didn't touch
  // (anything outside risk_score.*) so we don't silently lie.
  function formatRecActionMessage(action, payload) {
    if (action !== "accept") return `Marked ${action}`;
    const applied = payload && payload.applied;
    if (!applied) return "Accepted";
    if (applied.kind === "risk_config_upsert" && applied.row) {
      const r = applied.row;
      return "Accepted — risk_config for " + r.site_id + " now " +
        (r.inline_enforce ? "ENFORCING" : "observing") +
        " (challenge=" + r.challenge_threshold +
        ", deny=" + r.deny_threshold + ").";
    }
    if (applied.kind === "risk_config_route_upsert" && applied.row) {
      const r = applied.row;
      return "Accepted — route override " + r.method + " " + r.path_template +
        " now " + (r.inline_enforce ? "ENFORCING" : "observing") +
        " (challenge=" + r.challenge_threshold +
        ", deny=" + r.deny_threshold + ").";
    }
    return "Accepted — " + (applied.kind || "config updated") + ".";
  }

  const recAction = async (id, action) => {
    try {
      // Phase K.13: "revert" routes to the dedicated endpoint and
      // surfaces what was restored in the snackbar.
      if (action === "revert") {
        if (!window.confirm("Revert this recommendation? The previous risk_config will be restored and the rec marked rejected.")) return;
        const resp = await asec.recommendationRevert(siteId, id);
        showSuccess(formatRevertMessage(resp && resp.data));
        reload();
        return;
      }
      const reason = action !== "accept" ? window.prompt("Reason (optional)?") || "" : undefined;
      const resp = await asec.recommendationPatch(siteId, id, { action, reason });
      showSuccess(formatRecActionMessage(action, resp && resp.data));
      reload();
    } catch (e) {
      // K.10 + K.13 handlers emit 409/500 with { message } on
      // apply / revert failure; surface verbatim.
      const msg = (e && e.response && e.response.data && e.response.data.message)
        || (e && e.message) || String(e);
      showError(msg);
    }
  };

  // Phase K.13: snackbar text for a successful revert. Mirrors the
  // K.11 accept message style so before/after read symmetrically.
  function formatRevertMessage(payload) {
    const restored = payload && payload.restored;
    if (!restored) return "Reverted.";
    switch (restored.kind) {
      case "risk_config_restored":
        return "Reverted — risk_config for " + restored.row.site_id +
          " restored to " + (restored.row.inline_enforce ? "ENFORCING" : "observing") +
          " (challenge=" + restored.row.challenge_threshold +
          ", deny=" + restored.row.deny_threshold + ").";
      case "risk_config_deleted":
        return "Reverted — risk_config for " + restored.site_id +
          " deleted (no row existed before accept).";
      case "risk_config_route_restored":
        return "Reverted — route override " + restored.row.method + " " + restored.row.path_template +
          " restored to " + (restored.row.inline_enforce ? "ENFORCING" : "observing") +
          " (challenge=" + restored.row.challenge_threshold +
          ", deny=" + restored.row.deny_threshold + ").";
      case "risk_config_route_deleted":
        return "Reverted — route override " + restored.method + " " + restored.path_template +
          " deleted (no row existed before accept).";
      default:
        return "Reverted — " + (restored.kind || "config restored") + ".";
    }
  }

  return (
    <Stack spacing={3}>
      <Tabs value={sub} onChange={(_, v) => setSub(v)} sx={asecTabsSx}>
        <Tab label={`Findings${findings !== null ? ` (${findingsTotal})` : ""}`} />
        <Tab label={`Baselines${baselines !== null ? ` (${baselinesTotal})` : ""}`} />
        <Tab label={`Recommendations${recs !== null ? ` (${recs.length})` : ""}`} />
      </Tabs>

      {sub === 0 && (
        <Stack spacing={2}>
          <AsecEventsFilterBar
            trailing={(
              <RefreshIconButton
                onClick={reloadFindings}
                disabled={findings === null}
                loading={findings === null}
                size="compact"
              />
            )}
          >
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ ...filterSelectSx, minWidth: 180 }}
            >
              {FINDING_STATUS_FILTERS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </AsecEventsFilterBar>
          <Box sx={{ borderRadius: "8px", border: `1px solid ${colors.neutral[200]}`, overflow: "hidden" }}>
            <FindingsTable
              findings={findings}
              statusFilter={statusFilter}
              onOpen={setDrawerFinding}
            />
            <AnomalyFindingDrawer
              open={Boolean(drawerFinding)}
              onClose={() => setDrawerFinding(null)}
              row={drawerFinding}
              onResolve={resolveFinding}
              onSuppressRequest={setSuppressFindingId}
              actionSaving={actionSaving || suppressSaving}
            />
            <SuppressReasonDialog
              open={Boolean(suppressFindingId)}
              onClose={() => setSuppressFindingId(null)}
              onConfirm={confirmSuppress}
              saving={suppressSaving}
            />
            {findings !== null && (
              <TablePagination
                component="div"
                count={findingsTotal}
                page={findingsPage}
                onPageChange={(_, p) => setFindingsPage(p)}
                rowsPerPage={findingsPageSize}
                onRowsPerPageChange={(e) => {
                  setFindingsPageSize(parseInt(e.target.value, 10));
                  setFindingsPage(0);
                }}
                rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                sx={tablePaginationSx}
              />
            )}
          </Box>
        </Stack>
      )}

      {sub === 1 && (
        <Stack spacing={2}>
          <AsecEventsFilterBar
            search={baselineSearch}
            onSearchChange={setBaselineSearch}
            searchPlaceholder="Search host, path, or metric…"
            trailing={(
              <RefreshIconButton
                onClick={reloadBaselines}
                disabled={baselines === null}
                loading={baselines === null}
                size="compact"
              />
            )}
          >
            <Select
              value={baselineMetricFilter}
              onChange={(e) => setBaselineMetricFilter(e.target.value)}
              displayEmpty
              sx={{ ...filterSelectSx, minWidth: 160 }}
            >
              {BASELINE_METRIC_FILTERS.map((opt) => (
                <MenuItem key={opt.value || "all-metrics"} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
            <Select
              value={baselineMethodFilter}
              onChange={(e) => setBaselineMethodFilter(e.target.value)}
              displayEmpty
              sx={{ ...filterSelectSx, minWidth: 140 }}
            >
              {BASELINE_METHOD_FILTERS.map((opt) => (
                <MenuItem key={opt.value || "all-methods"} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
            <Select
              value={baselineConfidenceFilter}
              onChange={(e) => setBaselineConfidenceFilter(e.target.value)}
              displayEmpty
              sx={{ ...filterSelectSx, minWidth: 190 }}
            >
              {BASELINE_CONFIDENCE_FILTERS.map((opt) => (
                <MenuItem key={opt.value || "all-samples"} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </AsecEventsFilterBar>
          <Box sx={{ borderRadius: "8px", border: `1px solid ${colors.neutral[200]}`, overflow: "hidden" }}>
            <BaselinesTable
              baselines={baselines}
              allBaselines={baselines}
              emptyMessage={getBaselinesEmptyMessage({
                search: baselineSearch,
                metric: baselineMetricFilter,
                method: baselineMethodFilter,
                confidence: baselineConfidenceFilter,
              })}
            />
            {baselines !== null && (
              <TablePagination
                component="div"
                count={baselinesTotal}
                page={baselinesPage}
                onPageChange={(_, p) => setBaselinesPage(p)}
                rowsPerPage={baselinesPageSize}
                onRowsPerPageChange={(e) => {
                  setBaselinesPageSize(parseInt(e.target.value, 10));
                  setBaselinesPage(0);
                }}
                rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                sx={tablePaginationSx}
              />
            )}
          </Box>
        </Stack>
      )}

      {sub === 2 && (
        <Box sx={{ borderRadius: "8px", border: `1px solid ${colors.neutral[200]}`, overflow: "hidden" }}>
          <RecommendationsTable recs={recs} allRecs={recs} dryRuns={dryRuns} onAction={recAction} />
          {recs !== null && (
            <TablePagination
              component="div"
              count={recsTotal}
              page={recsPage}
              onPageChange={(_, p) => setRecsPage(p)}
              rowsPerPage={recsPageSize}
              onRowsPerPageChange={(e) => {
                setRecsPageSize(parseInt(e.target.value, 10));
                setRecsPage(0);
              }}
              rowsPerPageOptions={PAGE_SIZE_OPTIONS}
              sx={tablePaginationSx}
            />
          )}
        </Box>
      )}
    </Stack>
  );
}

export default function AsecAnomaly() {
  return <AsecPage featureKey="api.anomaly_engine" helpTooltip="Statistical anomaly detection (z-score, EWMA, percentile bands, Markov chains) over per-endpoint baselines, plus engine-generated recommendations. ML detection deferred to Phase 7.x." title="Anomaly">{(siteId) => <Body siteId={siteId} />}</AsecPage>;
}
